import { useMutation } from '@tanstack/react-query';

import { ActionData } from '@bonusly/common-action-input';
import { useDispatch } from '@bonusly/events';
import { useHttpClient } from '@bonusly/http-client';

import { GiveBoxEvent } from '../events';
import { createTrackingData } from '../utils/analytics';
import { createBonusData } from '../utils/create-bonus-data';
import { parseErrorMessage } from '../utils/error';
import { useBonusBoxAnalytics } from './use-bonus-box-analytics';

interface UseGiveRecognitionConfig {
	onError?: (error: unknown) => void;
	onSuccess?: VoidFunction;
	via?: string;
	msTeamsMeetingId?: string;
	wrapPost?: boolean;
}

export const useGiveRecognition = ({ onError, onSuccess, via, msTeamsMeetingId, wrapPost }: UseGiveRecognitionConfig = {}) => {
	const http = useHttpClient('v2');
	const analytics = useBonusBoxAnalytics();
	const dispatch = useDispatch();

	const { mutate, isLoading: isSubmitting } = useMutation<{ data: { result: any } }, unknown, ActionData, unknown>(
		(action) => {
			const data = {
				bonus: {
					...createBonusData({ action }),
					wrap_post: wrapPost,
				},
			};

			const params = {
				include_children: true,
			};

			if (via) {
				Object.assign(params, { application_name: via });
			}

			if (msTeamsMeetingId) {
				Object.assign(params, { ms_teams_meeting_id: msTeamsMeetingId });
			}

			return http.post('/bonuses', data, { params });
		},
		{
			onError: (err: unknown) => {
				analytics.trackSubmitFailure({
					reason: parseErrorMessage(err),
				});

				onError?.(err);
			},
			onSuccess: (data, vars) => {
				const bonus = data.data.result;
				const totalAmount = vars.totalAmount;

				dispatch(GiveBoxEvent.Created, { bonus, totalAmount });
				analytics.trackSubmitSuccess(createTrackingData(vars));
				onSuccess?.();
			},
		}
	);

	return {
		isSubmitting,
		onSubmit: mutate,
	};
};
